// Updated RouteGuard.tsx
import { useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

type UserRole = 'ems' | 'admin' | 'superadmin';

interface RoleConfig {
  home: string;
  paths: string[];
  inheritPaths?: string[]; // Paths from other roles to inherit
}

const RouteGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Define base paths for each role
  const baseRolePaths: Record<UserRole, RoleConfig> = {
    ems: {
      home: '/medical-emergencies',
      paths: [
        '/home',
        '/medical-emergencies',
        '/medical-emergencies/emergency',
        '/ems/medical-emergencies',
        '/ems/medical-emergencies/history',
        '/ems/medical-emergencies/initiate-medical',
        '/edit-profile',
        '/users/profile',
        '/call'
      ]
    },
    admin: {
      home: '/home',
      paths: [
        '/home',
        '/flights',
        '/medicalkit',
        '/users',
        '/passengers',
        '/passengers/addPassengers',
        '/passenger-details',
        '/edit-profile',
        '/users/profile',
        '/onboardingGuide',
        '/submittedforms',
        '/medicalEventReportFormList',
        '/FitToFly',
        '/FitToFlyflow',
        '/AddFitToFly'
      ],
      inheritPaths: ['ems'] // Admin inherits EMS paths
    },
    superadmin: {
      home: '/home',
      paths: [
        // Superadmin-specific paths
        '/roles',
        '/create-role',
        '/organization',
        '/AddSubscription',
        '/ViewSubscription',
        '/user-activity',
        '/coordinator'
      ],
      inheritPaths: ['admin', 'ems'] // SuperAdmin inherits all lower role paths
    }
  };

  // Public paths that don't require authentication
  const publicPaths = ['/', '/login', '/reset-password'];

  // Function to check if user is superadmin
  const isSuperadmin = (role: UserRole): boolean => {
    return role === 'superadmin';
  };

  // Function to get all allowed paths for a role (including inherited)
  const getAllowedPaths = (role: UserRole): string[] => {
    if (isSuperadmin(role)) {
      // Superadmins can access any path, so we return a special marker
      return ['*'];
    }

    const roleConfig = baseRolePaths[role];
    let paths = [...roleConfig.paths];
    
    if (roleConfig.inheritPaths) {
      roleConfig.inheritPaths.forEach(inheritedRole => {
        paths = [...paths, ...baseRolePaths[inheritedRole as UserRole].paths];
      });
    }
    
    return Array.from(new Set(paths)); // Remove duplicates
  };

  useEffect(() => {
    const currentPath = location.pathname;
    
    // Skip check for public paths
    if (publicPaths.includes(currentPath)) {
      return;
    }

    // Get user info from localStorage
    const userInfoJson = localStorage.getItem('userinfo');
    if (!userInfoJson) {
      navigate('/login');
      return;
    }

    try {
      const user = JSON.parse(userInfoJson);
      const userRole = user['roleLabel']?.toLowerCase() as UserRole;
      // console.log("userrole", userRole)

      // Type-safe role check
      if (!(userRole in baseRolePaths)) {
        navigate('/login');
        return;
      }

      // Get all allowed paths for this role
      const allowedPaths = getAllowedPaths(userRole);
      const homePath = baseRolePaths[userRole].home;

      // Superadmin bypass - can access any path
      if (isSuperadmin(userRole)) {
        return;
      }

      // Check if current path is allowed for non-superadmin roles
      const isAllowed = allowedPaths.some(allowedPath => 
        allowedPath === '*' || // Superadmin case (shouldn't reach here due to above check)
        currentPath === allowedPath || 
        currentPath.startsWith(`${allowedPath}/`) ||
        (allowedPath.includes(':') && 
         currentPath.startsWith(allowedPath.split(':')[0]))
      );

      if (!isAllowed) {
        navigate(homePath);
      }
    } catch (error) {
      console.error('Error checking route permissions:', error);
      navigate('/login');
    }
  }, [location.pathname, navigate]);

  return <Outlet />;
};

export default RouteGuard;